// src/pages/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sheet,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  Alert,
  Box
} from '@mui/joy';
import { KeyRound, User } from 'lucide-react';
import useAuthStore from '../stores/authStore';

interface LocationState {
  from?: {
    pathname: string;
  };
}

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  
  // Get state from auth store
  const { login, isAuthenticated, isLoading, error, clearError } = useAuthStore();
  
  // Get the return URL from location state or default to home
  const from = locationState?.from?.pathname || "/dashboard";
  
  // If already authenticated, redirect to the return URL
  useEffect(() => {
    // Only redirect if authenticated AND there's no error
    if (isAuthenticated && !error) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from, error]);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    // Validate form
    if (!username.trim() || !password.trim()) {
      return;
    }
    
    // Try to login
    const success = await login(username, password);
    
    if (success) {
      // Login successful, navigation will happen in useEffect
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.body'
      }}
    >
      <Sheet
        sx={{
          width: 400,
          p: 4,
          borderRadius: 'md',
          boxShadow: 'md'
        }}
      >
        <Stack spacing={3}>
          <Typography level="h4" component="h1" textAlign="center">
            Log in to your account
          </Typography>
          
          {error && (
            <Alert color="danger" variant="soft">
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Username</FormLabel>
                <Input
                  startDecorator={<User size={16} />}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input
                  startDecorator={<KeyRound size={16} />}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </FormControl>
              
              <Button
                type="submit"
                loading={isLoading}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? 'Logging in...' : 'Log in'}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Sheet>
    </Box>
  );
};

export default LoginPage;